import { useState, useEffect, useCallback } from "react";
import ContainerCardAlert from "./ContainerCardAlert";
import ButtonTray from "./ButtonTray";
import { useSelector } from "react-redux";

interface ContainerRecommendationListProps {
  containers?: any[];
  selectedTask?: any;
  numberOfRecommendations?: any;
  data?: any;
}

export const ContainerRecommendationList = ({
  containers = [],
  selectedTask,
  numberOfRecommendations,
  data,
}: ContainerRecommendationListProps) => {
  const userEmail: any = useSelector((state: any) => state.auth?.user?.email);
  const isSam = userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai";
  const startingButtonSelection = isSam ? "component" : "container";
  const [selectedButton, setSelectedButton] = useState(startingButtonSelection);

  // State for API data
  const [apiData, setApiData] = useState<any>({
    movement: { containers: {} },
    price: { containers: {} }
  });
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch all recommendation data once at page level
  const fetchAllRecommendations = useCallback(async () => {
    try {
      setLoading(true);

      const [movementResp, priceResp] = await Promise.all([
        fetch('https://api.stag.kimaru.ai/api/v1/movement_recommendation/get_recommendation', {
          headers: {
            accept: 'application/json',
            'user-email': userEmail
          }
        }),
        fetch('https://api.stag.kimaru.ai/api/v1/price_recommendation/get_recommendation', {
          headers: {
            accept: 'application/json',
            'user-email': userEmail
          }
        })
      ]);

      const movementData: any = movementResp.ok ? await movementResp.json() : { containers: {} };
      const priceData: any = priceResp.ok ? await priceResp.json() : { containers: {} };

      setApiData({
        movement: movementData,
        price: priceData
      });
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setApiData({
        movement: { containers: {} },
        price: { containers: {} }
      });
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (userEmail) {
      fetchAllRecommendations();
    }
  }, [userEmail, refreshTrigger, fetchAllRecommendations]);

  // Function to trigger refresh from child components
  const handleRefreshData = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const title = `${numberOfRecommendations} Recommendations | ${containers.length} ${selectedButton ? selectedButton.charAt(0).toUpperCase() + selectedButton.slice(1) + "s" : "Containers"}`;
  const today = new Date();
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const formattedDate = `${monthNames[today.getMonth()]} ${today.getDate()}`;

  const processedContainers = containers.map((container: any, index: any) => {
    const containerData = container;
    const recommendations = containerData.recommendations || [];
    const totalUnits = recommendations.reduce(
      (sum: any, item: any) => sum + item.quantity_on_hand,
      0
    );
    const capacityPercentage = Math.round((totalUnits / 500) * 100);
    const sortedByStock = [...recommendations]
      .sort((a: any, b: any) => a.quantity_on_hand - b.quantity_on_hand)
      .slice(0, 3)
      .map((item: any) => ({
        name: item.item_name,
        quantity: item.quantity_on_hand,
        percentage: Math.round((item.quantity_on_hand / 35) * 100),
      }));
    const specialData = {
      totalUnits,
      lastRestocked: formattedDate,
      capacityPercentage,
      lowStockItems: sortedByStock,
    };

    // Get API data for this specific container
    const containerId = container.container_id;
    const movementRecs = apiData.movement?.containers?.[containerId]?.recommendations || [];
    const priceRecs = apiData.price?.containers?.[containerId]?.recommendations || [];

    // Calculate total count from API data
    const totalRecommendationCount = movementRecs.length + priceRecs.length;

    // Check if container is completed (no recommendations from API)
    const isCompleted = totalRecommendationCount === 0 && recommendations.length === 0;

    return {
      ...container,
      containerData: {
        ...containerData,
        specialData,
      },
      apiRecommendations: {
        movement: movementRecs,
        price: priceRecs,
        totalCount: totalRecommendationCount,
        isCompleted
      }
    };
  });

  const handleButtonChange = (buttonId: any) => {
    setSelectedButton(buttonId);
    console.log(`Switched to: ${buttonId}`);
  };

  const isContainerCompleted = (container: any) => {
    return container.apiRecommendations?.isCompleted || false;
  };

  // Sort containers: completed ones first (maintaining their relative order), then active ones
  const sortedContainers = [...processedContainers].sort((a: any, b: any) => {
    const aCompleted = isContainerCompleted(a);
    const bCompleted = isContainerCompleted(b);

    if (aCompleted !== bCompleted) {
      return aCompleted ? -1 : 1;
    }

    return 0;
  });

  if (loading) {
    return (
      <div className="p-2 bg-white min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading recommendations...</div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-white min-h-screen">
      <div className="mx-auto space-y-2">
        {/* Header with title and button tray */}
        <div className="flex justify-between items-center py-1 px-2">
          <h3 className="font-semibold text-lg">{title}</h3>
          <ButtonTray
            selectedButton={selectedButton}
            onButtonChange={handleButtonChange}
          />
        </div>
        {/* Container cards */}
        {sortedContainers.map((container: any, index: any) => (
          <ContainerCardAlert
            key={`${container.container_id}-${index}`}
            container={container}
            type={"sell-through"}
            buttonDisabled={false}
            containerData={container.containerData}
            selectedLens={selectedButton}
            apiRecommendations={container.apiRecommendations}
            onDataRefresh={handleRefreshData}
          />
        ))}
      </div>
    </div>
  );
};

export default ContainerRecommendationList;